import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  Typography,
  Input,
  Button,
  FormControl,
  FormLabel,
  Alert,
  LinearProgress
} from '@mui/joy';
import { Envelope as Email, ArrowLeft as ArrowBack, CheckCircle } from '@phosphor-icons/react';
import { authApi } from '../../lib/api';
import { PasswordResetConfirmPage } from './PasswordResetConfirmPage';

export const PasswordResetPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  // If there's a token, show the confirm page instead
  if (token) {
    return <PasswordResetConfirmPage />;
  }
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await authApi.requestPasswordReset(email);
      setSent(true);
    } catch (err: any) {
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          p: 2
        }}
      >
        <Card sx={{ maxWidth: 400, width: '100%', textAlign: 'center', p: 3 }}>
          <CheckCircle color="var(--joy-palette-success-500)" size={64} style={{ marginBottom: 16 }} />
          <Typography level="h2" sx={{ mb: 2 }}>
            Check Your Email
          </Typography>
          <Typography level="body-md" sx={{ mb: 3, color: 'text.secondary' }}>
            We've sent password reset instructions to <strong>{email}</strong>
          </Typography>
          <Button
            component={Link}
            to="/login"
            variant="outlined"
            startDecorator={<ArrowBack />}
          >
            Back to Login
          </Button>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        p: 2
      }}
    >
      <Card sx={{ maxWidth: 400, width: '100%', boxShadow: 'lg' }}>
        {/* Header */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #7013f5 0%, #5a0fc8 100%)',
            color: 'white',
            p: 3,
            textAlign: 'center'
          }}
        >
          <Typography level="h2" sx={{ fontWeight: 300, fontSize: '1.5rem', mb: 1 }}>
            Reset Password
          </Typography>
          <Typography level="body-md" sx={{ opacity: 0.75 }}>
            Enter your email to receive reset instructions
          </Typography>
        </Box>

        {/* Body */}
        <Box sx={{ p: 3 }}>
          {loading && <LinearProgress sx={{ mb: 2 }} />}
          
          {error && (
            <Alert color="danger" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <FormControl sx={{ mb: 3 }}>
              <FormLabel>Email Address</FormLabel>
              <Input
                type="email"
                startDecorator={<Email />}
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
              />
            </FormControl>

            <Button
              type="submit"
              fullWidth
              size="lg"
              loading={loading}
              sx={{
                background: 'linear-gradient(135deg, #7013f5 0%, #5a0fc8 100%)',
                mb: 2,
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a0fc8 0%, #4a0da8 100%)',
                }
              }}
            >
              Send Reset Instructions
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Button
                component={Link}
                to="/login"
                variant="plain"
                startDecorator={<ArrowBack />}
                size="sm"
              >
                Back to Login
              </Button>
            </Box>
          </form>
        </Box>
      </Card>
    </Box>
  );
};